import React, { useState, useEffect } from 'react'
import { FeatherLoader, useErrorService } from '@abroad/components'
import API from '../../utils/API'
import { Toggle } from '../common'

const MyCalendarsTab = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [calendarsData, setCalendarsData] = useState([])
  const [updatingCalendars, setUpdatingCalendars] = useState(new Set())
  const Error = useErrorService()

  useEffect(() => {
    syncAndFetchCalendars()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const syncAndFetchCalendars = async () => {
    try {
      // Show unified loading state for both sync and fetch operations
      setIsLoading(true)
      const syncResponse = await API.CoachAPI.coachUser.syncCalendars()

      if (syncResponse?.data?.success) {
        // If sync is successful, fetch calendars
        const calendarsResponse = await API.CoachAPI.coachUser.getCalendars()

        if (calendarsResponse?.data?.success && calendarsResponse?.data?.data) {
          setCalendarsData(calendarsResponse.data.data)
        }
      }
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleChange = async (accountId, calendarId, isSelected) => {
    const newIsSelected = !isSelected

    // Optimistically update UI
    setCalendarsData((prevData) =>
      prevData.map((item) => {
        if (item.account.id === accountId) {
          return {
            ...item,
            account: {
              ...item.account,
              calendars: item.account.calendars.map((cal) =>
                cal.id === calendarId
                  ? { ...cal, isSelected: newIsSelected }
                  : cal,
              ),
            },
          }
        }
        return item
      }),
    )

    // Track updating state
    setUpdatingCalendars((prev) => new Set(prev).add(calendarId))

    try {
      const response = await API.CoachAPI.coachUser.updateCalendarSelection(
        calendarId,
        {
          isSelected: newIsSelected,
        },
      )

      if (!response?.data?.success) {
        // Revert on failure
        setCalendarsData((prevData) =>
          prevData.map((item) => {
            if (item.account.id === accountId) {
              return {
                ...item,
                account: {
                  ...item.account,
                  calendars: item.account.calendars.map((cal) =>
                    cal.id === calendarId
                      ? { ...cal, isSelected: isSelected }
                      : cal,
                  ),
                },
              }
            }
            return item
          }),
        )
        Error.showError(new Error('Failed to update calendar selection'))
      }
    } catch (e) {
      // Revert on error
      setCalendarsData((prevData) =>
        prevData.map((item) => {
          if (item.account.id === accountId) {
            return {
              ...item,
              account: {
                ...item.account,
                calendars: item.account.calendars.map((cal) =>
                  cal.id === calendarId
                    ? { ...cal, isSelected: isSelected }
                    : cal,
                ),
              },
            }
          }
          return item
        }),
      )
      Error.showError(e)
    } finally {
      setUpdatingCalendars((prev) => {
        const newSet = new Set(prev)
        newSet.delete(calendarId)
        return newSet
      })
    }
  }

  // Group calendars by account email
  const groupedCalendars = calendarsData.map((item) => ({
    accountId: item.account.id,
    accountEmail: item.account.email,
    accountProvider: item.account.provider,
    isAbroadCalendar: item.account.isAbroadCalendar,
    calendars: item.account.calendars
      .map((calendar) => ({
        calendarId: calendar.id,
        calendarName: calendar.name,
        isSelected: calendar.isSelected,
        isDefault: calendar.isDefault,
      }))
      .sort((a, b) => {
        // Default calendars always show on top
        if (a.isDefault && !b.isDefault) return -1
        if (!a.isDefault && b.isDefault) return 1
        return 0
      }),
  }))

  if (isLoading) {
    return (
      <div className='text-center py-5'>
        <FeatherLoader />
        <p className='mt-3'>Loading your calendars...</p>
      </div>
    )
  }

  if (groupedCalendars.length === 0) {
    return (
      <div className='text-center py-5'>
        <p className='s6'>No calendars found.</p>
      </div>
    )
  }

  return (
    <div className='mt-3'>
      {groupedCalendars.map((account, accountIndex) => (
        <div key={account.accountId} className='mb-4'>
          <div className='mb-3'>
            <h5 className='s3 text-break'>
              {account.accountEmail} :{' '}
              {account.isAbroadCalendar
                ? 'Abroad'
                : account.accountProvider.charAt(0).toUpperCase() +
                  account.accountProvider.slice(1)}{' '}
              Calendar
            </h5>
          </div>
          <div className='pl-3'>
            {account.calendars.map((calendar, calendarIndex) => (
              <div
                key={calendar.calendarId}
                className='d-flex align-items-center pb-3'>
                <div>
                  <Toggle
                    id={`toggle-${account.accountId}-${calendar.calendarId}`}
                    checked={calendar.isSelected}
                    onChange={() =>
                      handleToggleChange(
                        account.accountId,
                        calendar.calendarId,
                        calendar.isSelected,
                      )
                    }
                    disabled={
                      calendar.isDefault ||
                      updatingCalendars.has(calendar.calendarId)
                    }
                    disableOpacity={calendar.isDefault}
                    // label={calendar.isSelected ? 'On' : 'Off'}
                    // isToggleWithLabel={true}
                  />
                </div>
                <div className='flex-grow-1 ml-3'>
                  <span className='s6 d-flex'>
                    <span>{calendar.calendarName}</span>
                    <pre className='my-0'>
                      {calendar.isDefault && ' (Default)'}
                    </pre>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default MyCalendarsTab
